import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  type GISStateLayer,
  gisLayerFor,
  gisSublayerFor,
  loadGISLayer,
  selectGISLayers,
  updateLayer,
} from '@rsos/reduxDataStore';
import type { RootState } from '../../../../app';
import {
  type AliasMatch,
  findLayerSemanticEntry,
  matchLayerAlias,
  normalizeServiceUrl,
} from './layerAliasMatcher';
import {
  type AIClassification,
  type LayerCandidate,
  classifyQueryAsLayer,
} from './layerNameAI';

const AI_DEBOUNCE_MS = 250;
const AI_MIN_QUERY_LENGTH = 3;
const PARCEL_LAYER_PATTERN = 'parcel';
/**
 * Visible duration of the "Turn on <layer>" suggestion's countdown
 * bar AND the auto-dismiss window — they're one and the same. The
 * dropdown renders a CSS fill animation of this duration on the
 * chip, and the chip's `onAnimationEnd` is what actually triggers
 * the dismissal (see `dismissSuggestion`). Keeping a single source
 * of truth means the bar is guaranteed to reach 100% at the exact
 * frame the chip is removed — no dual-timer drift to manage.
 */
export const LAYER_SUGGESTION_DISMISS_MS = 10000;

export interface LayerSuggestion {
  layer: GISStateLayer;
  layerName: string;
  /** Alias / heuristic that produced this match — surfaced as a tooltip. */
  matchedAlias: string;
  /** Override for the CTA button copy when set; default is `Turn on <name>`. */
  prompt?: string;
}

/**
 * Prefer the redux `layer.title` (admin-configured `service.display_name`)
 * over the metadata friendly name — each PSAP can rename the same upstream
 * service to whatever their dispatchers call it locally, and the CTA must
 * match what's in the layer-control panel.
 */
function resolveLayerDisplayName(layer: GISStateLayer): string {
  if (layer.title) return layer.title;
  const entry = findLayerSemanticEntry(layer.service?.gis_service_url);
  return entry?.layer_name || '';
}

interface UseLayerSuggestionResult {
  suggestion: LayerSuggestion | null;
  isActivating: boolean;
  activate: () => Promise<void>;
  /**
   * Mark a layer as dismissed so its suggestion stops surfacing for
   * the current query. Wired to the chip's CSS-animation `end` event
   * by the dropdown — that's how the auto-dismiss countdown actually
   * fires. Safe to call with a stale id (no-op if already dismissed
   * or if the suggestion has moved on).
   */
  dismissSuggestion: (layerId: string) => void;
}

/**
 * Resolve the current query into an inactive-layer "turn on X" suggestion.
 * Tries the AI classifier first (layer-name hit, then human-name → parcel
 * fallback) and falls back to the synchronous alias matcher.
 *
 * Activation mirrors the on-demand load + sublayer visibility sync from
 * `GISLayerControl.tsx` so a search-driven turn-on behaves identically to
 * a manual checkbox click.
 */
export default function useLayerSuggestion(
  query: string,
): UseLayerSuggestionResult {
  const dispatch =
    useDispatch<ThunkDispatch<RootState, undefined, UnknownAction>>();
  const layers = useSelector(selectGISLayers) as GISStateLayer[];
  const [isActivating, setIsActivating] = useState(false);
  const [aiMatch, setAiMatch] = useState<AIClassification | null>(null);
  // True while the AI classifier is debouncing or in-flight for the
  // *current* query. We hold the suggestion chip back until this flips
  // false so the chip never flashes an alias-based guess that the AI
  // then overrides ~250ms later — a single, settled answer instead of
  // a flicker. When AI is ineligible (query too short, no candidates)
  // this stays false, so synchronous alias matches still render
  // immediately.
  const [aiPending, setAiPending] = useState(false);
  // Layer ids the user has implicitly dismissed (the auto-dismiss timer
  // fired). Suppressed from `suggestion` so a re-render with the same
  // query doesn't bring the chip back. Cleared whenever the query goes
  // empty so a fresh search starts from a clean slate.
  const [dismissedLayerIds, setDismissedLayerIds] = useState<Set<string>>(
    () => new Set(),
  );

  const aliasMatch: AliasMatch | null = useMemo(
    () => matchLayerAlias(query),
    [query],
  );

  // Only inactive layers are valid CTA targets.
  const aiCandidates = useMemo<LayerCandidate[]>(
    () =>
      layers
        .filter(l => !l.active)
        .map(l => ({ id: l.id, name: l.title }))
        .filter(c => c.name && c.name.length > 0),
    [layers],
  );

  // Stable dependency for the effect — selector identity changes shouldn't
  // re-fire the AI call when the actual candidate set hasn't changed.
  const candidatesKey = useMemo(
    () =>
      aiCandidates
        .map(c => c.id)
        .sort()
        .join(','),
    [aiCandidates],
  );

  useEffect(() => {
    // Clear synchronously so a previous query's verdict doesn't continue
    // rendering against the new query while the new debounced call is in
    // flight.
    setAiMatch(null);
    const trimmed = query.trim();
    if (trimmed.length < AI_MIN_QUERY_LENGTH || aiCandidates.length === 0) {
      // AI won't run for this query — no "settling" period to wait
      // through, so let any synchronous alias match render immediately.
      setAiPending(false);
      return undefined;
    }
    // From here on we *will* call the classifier (after the debounce).
    // Flag pending right away so the chip stays hidden through the
    // entire debounce + network window — anything else flickers.
    setAiPending(true);
    const controller = new AbortController();
    const timer = setTimeout(() => {
      classifyQueryAsLayer(trimmed, aiCandidates, controller.signal)
        .then(result => {
          if (controller.signal.aborted) return;
          setAiMatch(result);
          setAiPending(false);
        })
        .catch(() => {
          // Network/parse failure — treat as "no AI verdict" and let
          // the alias fallback take over instead of leaving the chip
          // suppressed forever.
          if (!controller.signal.aborted) setAiPending(false);
        });
    }, AI_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `aiCandidates` is captured via the stable `candidatesKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, candidatesKey]);

  const suggestion = useMemo<LayerSuggestion | null>(() => {
    // Wait for the AI verdict before committing to a chip. Without
    // this, the synchronous alias match would render first and then
    // get swapped (or removed) the moment the classifier returns —
    // a visible flicker for every search that's eligible for AI.
    if (aiPending) return null;

    let computed: LayerSuggestion | null = null;

    if (aiMatch?.layerId) {
      const layer = layers.find(l => l.id === aiMatch.layerId);
      if (layer && !layer.active) {
        const displayName = resolveLayerDisplayName(layer);
        computed = {
          layer,
          layerName: displayName,
          matchedAlias: displayName,
        };
      }
    }

    // Human-name → suggest a parcel-style layer so the user can search
    // owner / occupant fields.
    if (!computed && aiMatch?.isHumanName) {
      const parcelLayer = layers.find(
        l =>
          !l.active &&
          (l.title?.toLowerCase().includes(PARCEL_LAYER_PATTERN) ?? false),
      );
      if (parcelLayer) {
        const displayName = resolveLayerDisplayName(parcelLayer);
        computed = {
          layer: parcelLayer,
          layerName: displayName,
          matchedAlias: 'name detected',
          prompt: displayName,
        };
      }
    }

    if (!computed && aliasMatch) {
      const target = normalizeServiceUrl(aliasMatch.entry.layer_url);
      const layer = target
        ? layers.find(
            l => normalizeServiceUrl(l.service?.gis_service_url) === target,
          )
        : undefined;
      if (layer && !layer.active) {
        computed = {
          layer,
          layerName: resolveLayerDisplayName(layer),
          matchedAlias: aliasMatch.matchedAlias,
        };
      }
    }

    // Suppress anything the auto-dismiss timer already fired against —
    // otherwise a downstream re-render would just resurrect the chip.
    if (computed && dismissedLayerIds.has(computed.layer.id)) return null;
    return computed;
  }, [aiPending, aiMatch, aliasMatch, layers, dismissedLayerIds]);

  // The auto-dismiss countdown is *the CSS fill animation on the
  // chip itself* — the dropdown calls `dismissSuggestion` from the
  // chip's `onAnimationEnd`, so the bar finishing visually IS the
  // dismissal trigger. No JS setTimeout to keep in sync; one source
  // of truth.
  const dismissSuggestion = useCallback((layerId: string) => {
    setDismissedLayerIds(prev => {
      if (prev.has(layerId)) return prev;
      const next = new Set(prev);
      next.add(layerId);
      return next;
    });
  }, []);

  // Forget previous dismissals on any query change so the chip can
  // return for the next search. Without this a layer dismissed at
  // query "fire" would stay suppressed when the user edits to
  // "fires", which feels broken. (The dropdown also re-keys the
  // chip on `${layer.id}|${query}` so the animation restarts on each
  // keystroke — together this gives "5s of inactivity" semantics.)
  useEffect(() => {
    setDismissedLayerIds(prev => (prev.size === 0 ? prev : new Set()));
  }, [query]);

  const activate = useCallback(async () => {
    if (!suggestion) return;
    const { layer } = suggestion;
    setIsActivating(true);
    try {
      let resolvedLayer: GISStateLayer = layer;

      if (!layer.loaded && !layer.loading) {
        const result = await dispatch(
          loadGISLayer({ service: layer.service }),
        ).unwrap();
        resolvedLayer = result.loadedLayer;
      }

      const gisLayer = gisLayerFor(resolvedLayer);
      if (gisLayer) {
        gisLayer.visible = true;
        const excludedIds = resolvedLayer.filteredSublayerIds || [];
        for (const sl of resolvedLayer.sublayers) {
          const gisSublayer = gisSublayerFor(sl);
          if (!gisSublayer) continue;
          gisSublayer.visible = !excludedIds.includes(sl.uniqueId);
        }
      }

      dispatch(updateLayer({ layerID: resolvedLayer.id, active: true }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to activate suggested layer:',
        suggestion.layerName,
        error,
      );
    } finally {
      setIsActivating(false);
    }
  }, [dispatch, suggestion]);

  return { suggestion, isActivating, activate, dismissSuggestion };
}
